/**
 * Thin typed wrapper around the simulation Web Worker.
 *
 * Exposes a single `runInWorker` function that sends a message to the worker
 * and returns a Promise. A module-level singleton worker is created on first use.
 *
 * Falls back with a rejected Promise when `Worker` is not available (e.g. Jest /
 * jsdom environments), allowing callers to catch and run synchronous fallbacks.
 */

type WorkerAction = 'normalizeActivities' | 'parseRidePoints' | 'parseActivitiesFromSolution' | 'buildRidePoints';

interface WorkerRequest {
  id: number;
  action: WorkerAction;
  payload: unknown;
}

interface WorkerResponse {
  id: number;
  result: unknown;
  error?: string;
}

let _worker: Worker | null = null;
let _nextId = 0;
const _pending = new Map<number, { resolve: (v: unknown) => void; reject: (e: Error) => void }>();

function getWorker(): Worker | null {
  if (_worker) return _worker;
  if (typeof Worker === 'undefined') return null;
  try {
    _worker = new Worker(new URL('./simulationWorker.ts', import.meta.url));
    _worker.onmessage = (e: MessageEvent<WorkerResponse>) => {
      const { id, result, error } = e.data;
      const pending = _pending.get(id);
      if (!pending) return;
      _pending.delete(id);
      if (error) {
        pending.reject(new Error(error));
      } else {
        pending.resolve(result);
      }
    };
    _worker.onerror = () => {
      _pending.forEach(({ reject }) => reject(new Error('Simulation worker encountered a fatal error')));
      _pending.clear();
      _worker = null;
    };
    return _worker;
  } catch {
    return null;
  }
}

/**
 * Run a named transform action in the simulation Web Worker.
 *
 * Parameters:
 *   action (WorkerAction): The transform to execute.
 *   payload (unknown): Data to pass to the worker (must be structured-cloneable).
 *
 * Returns:
 *   Promise<unknown> — resolves with the transform result, or rejects if the
 *   worker is unavailable or the transform throws.
 */
export function runInWorker(action: WorkerAction, payload: unknown): Promise<unknown> {
  const worker = getWorker();
  if (!worker) {
    return Promise.reject(new Error('Simulation worker not available'));
  }
  return new Promise((resolve, reject) => {
    const id = _nextId++;
    _pending.set(id, { resolve, reject });
    worker.postMessage({ id, action, payload } as WorkerRequest);
  });
}
